<template>
  <div class="access-code-prompt" @click="cancel">
    <div class="access-code-prompt__panel" :class="{ 'access-code-prompt__panel--shake': isShaking }" @click.stop>
      <div class="access-code-prompt__header">
        <p>Enter the access PIN to continue.</p>
      </div>

      <div class="access-code-prompt__display">
        {{ maskedCode }}
      </div>

      <div class="access-code-prompt__keys">
        <button v-for="key in keys" :key="key" class="touch" @click="addKey(key)">
          {{ key }}
        </button>
        <button class="touch" @click="cancel">Back</button>
        <button class="touch" @click="addKey('0')">0</button>
        <button class="touch" @click="clearCode">Clear</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AccessCodePrompt",

  props: {
    prompt: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      code: "",
      isShaking: false,
      keys: ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
    };
  },

  computed: {
    maskedCode() {
      return this.code ? this.code.replace(/./g, "•") : "----";
    },
  },

  watch: {
    prompt() {
      this.clearCode();
    },
  },

  methods: {
    addKey(key) {
      if (this.code.length >= 4) return;
      this.code += key;

      if (this.code.length === 4) {
        this.submit();
      }
    },

    clearCode() {
      this.code = "";
      this.isShaking = false;
    },

    submit() {
      if (this.code === String(this.prompt.accessCode || "")) {
        this.$store.commit("UNLOCK_ACCESS_CODE_ENTITY", this.prompt.entityId);
        this.$store.commit("CLOSE_ACCESS_CODE_PROMPT");

        if (this.prompt.pendingRoute) {
          this.$router.push(this.prompt.pendingRoute).catch(() => {});
        }
        return;
      }

      this.code = "";
      this.shake();
    },

    shake() {
      this.isShaking = false;
      this.$nextTick(() => {
        this.isShaking = true;

        setTimeout(() => {
          this.isShaking = false;
        }, 420);
      });
    },

    cancel() {
      this.$store.commit("CLOSE_ACCESS_CODE_PROMPT");

      if (this.prompt.isCurrentRoute) {
        this.$router.push("/select").catch(() => {});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.access-code-prompt {
  align-items: center;
  background: rgba(0, 0, 0, 0.78);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  pointer-events: all;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 100000;

  &__panel {
    background: var(--access-code-background-color, rgba(12, 12, 14, 0.96));
    border: 0.1rem solid var(--access-code-border-color, rgba(255, 255, 255, 0.7));
    border-radius: var(--access-code-border-radius, 0.5rem);
    box-shadow: 0 1.2rem 4rem rgba(0, 0, 0, 0.45);
    color: var(--access-code-text-color, #fff);
    padding: 3rem;
    text-align: center;
    width: 32rem;

    &--shake {
      animation: accessCodeShake 0.42s ease;
    }
  }

  &__header {
    h2 {
      color: var(--access-code-text-color, #fff);
      font-size: 2.4rem;
      font-weight: var(--font-weight-secondary);
      margin: 0 0 0.8rem;
      text-transform: uppercase;
    }

    p {
      color: var(--access-code-text-color, #fff);
      font-size: 1.2rem;
      font-weight: var(--font-weight-secondary);
      margin: 0 0 2rem;
    }
  }

  &__display {
    align-items: center;
    background: transparent;
    border: 0.1rem solid var(--access-code-border-color, rgba(255, 255, 255, 0.7));
    border-radius: var(--access-code-border-radius, 0.5rem);
    color: var(--access-code-text-color, #fff);
    display: flex;
    font-size: 2rem;
    height: 4.5rem;
    justify-content: center;
    letter-spacing: 0.4rem;
    margin-bottom: 1.2rem;
  }

  &__keys {
    display: grid;
    gap: 0.8rem;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 1rem;
  }

  &__keys button {
    background: var(--access-code-button-color, transparent);
    border: 0.1rem solid var(--access-code-border-color, rgba(255, 255, 255, 0.7));
    border-radius: var(--access-code-border-radius, 0.5rem);
    color: var(--access-code-text-color, #fff);
    font-size: 1.4rem;
    height: 4.5rem;
    text-transform: uppercase;

    &:active {
      filter: brightness(1.35);
    }
  }
}

@keyframes accessCodeShake {
  0%,
  100% {
    transform: translateX(0);
  }

  15%,
  45%,
  75% {
    transform: translateX(-0.6rem);
  }

  30%,
  60%,
  90% {
    transform: translateX(0.6rem);
  }
}
</style>
